import { useMemo, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import {
  useDownloadProjectTimeEntriesCsv,
  useProject,
  useProjectTimeRollup,
  useWorkspace,
  type ReportRangeArgs,
} from '@fluxter/shared';
import { Download } from 'lucide-react';
import { ProjectPageHeader } from '@/components/ProjectPageHeader';
import { ProjectViewSwitcher } from '@/components/ProjectViewSwitcher';
import { ReportRangePicker } from '@/components/ReportRangePicker';
import { useSetTopBarSlots } from '@/components/TopBarSlots';
import { SkeletonList, SkeletonStatCards } from '@/components/Skeletons';
import { cn } from '@/lib/utils';

interface ProjectAttrs {
  name?: string;
}

interface BucketRow {
  user_id?: string;
  task_id?: string;
  name?: string;
  email?: string;
  title?: string;
  minutes?: number;
  billable_minutes?: number;
  amount_cents?: number;
}

interface RollupData {
  data?: {
    project_name?: string;
    billing_type?: 'none' | 'flat' | 'hourly';
    flat_fee_cents?: number | null;
    currency?: string;
    totals?: { minutes: number; billable_minutes: number; amount_cents: number };
    by_user?: BucketRow[];
    by_task?: BucketRow[];
  };
}

const defaultRange = (): ReportRangeArgs => {
  const now = new Date();
  const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
};

const formatHours = (minutes: number | undefined | null): string => {
  if (!minutes || minutes <= 0) return '0h';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

const formatMoney = (cents: number | undefined | null, currency: string | undefined): string => {
  if (cents === undefined || cents === null) return '—';
  if (!currency) return (cents / 100).toFixed(2);
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
  } catch {
    return (cents / 100).toFixed(2);
  }
};

export const ProjectTimePage = () => {
  const { workspaceSlug, projectSlug } = useParams({
    from: '/w/$workspaceSlug/p/$projectSlug/time',
  });
  const workspace = useWorkspace(workspaceSlug);
  const project = useProject(workspaceSlug, projectSlug);
  const [range, setRange] = useState<ReportRangeArgs>(() => defaultRange());

  const rollup = useProjectTimeRollup(workspaceSlug, projectSlug, range);
  const download = useDownloadProjectTimeEntriesCsv(workspaceSlug, projectSlug);

  const workspaceName = (workspace.data as { data?: { attributes?: { name?: string } } } | undefined)
    ?.data?.attributes?.name;
  const projectName = (project.data as { data?: { attributes?: ProjectAttrs } } | undefined)
    ?.data?.attributes?.name;

  const data = (rollup.data as RollupData | undefined)?.data;
  const totals = data?.totals;
  const currency = data?.currency;
  const byUser = useMemo(() => data?.by_user ?? [], [data]);
  const byTask = useMemo(() => data?.by_task ?? [], [data]);

  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownload = async () => {
    setDownloadError(null);
    try {
      await download.mutateAsync(range);
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : 'Could not start download.');
    }
  };

  // Topbar contributions — ViewSwitcher in right slot, "Download CSV"
  // in cta slot (matches the "+ New task" / "+ New note" CTA pattern).
  const topBarRight = useMemo(
    () => (
      <ProjectViewSwitcher
        workspaceSlug={workspaceSlug}
        projectSlug={projectSlug}
        current="time"
      />
    ),
    [workspaceSlug, projectSlug],
  );
  const topBarCta = useMemo(
    () => (
      <button
        type="button"
        onClick={() => void handleDownload()}
        disabled={download.isPending}
        className={cn(
          'inline-flex h-7 cursor-pointer items-center gap-1.5 rounded-lg border-0 px-2.5 text-[12.5px] font-medium text-white transition-shadow duration-100',
          'bg-[var(--primary)]',
          'shadow-[0_2px_8px_oklch(0.3_0.06_245/0.3)] hover:shadow-[0_3px_10px_oklch(0.3_0.06_245/0.4)]',
          'dark:shadow-[0_2px_12px_oklch(0.55_0.12_245/0.4)]',
          'disabled:opacity-50',
        )}
      >
        <Download className="size-3.5" />
        {download.isPending ? 'Preparing…' : 'Download CSV'}
      </button>
    ),
    // handleDownload re-binds per render; intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [download.isPending],
  );
  useSetTopBarSlots({ right: topBarRight, cta: topBarCta });

  return (
    <>
      <div className="space-y-6">
        <ProjectPageHeader
          workspaceSlug={workspaceSlug}
          workspaceName={workspaceName}
          projectSlug={projectSlug}
          projectName={projectName}
          current="time"
        />

        <header>
          <h1 className="text-foreground text-[28px] font-semibold tracking-[-0.025em]">
            Time roll-up
          </h1>
          <p className="text-foreground-faint mt-1 text-[12.5px]">
            Hours logged by user and by task. Flat-fee projects show zero amount here —
            the flat fee is on the workspace billable summary.
          </p>
        </header>

        {downloadError ? (
          <p className="text-destructive text-sm" role="alert">
            {downloadError}
          </p>
        ) : null}

        <ReportRangePicker value={range} onChange={setRange} />

        {rollup.isLoading ? (
          <div className="space-y-6">
            <SkeletonStatCards count={3} />
            <SkeletonList rows={4} />
            <SkeletonList rows={4} />
          </div>
        ) : (
          <>
            <SummaryCards
              totalMinutes={totals?.minutes ?? 0}
              billableMinutes={totals?.billable_minutes ?? 0}
              amountCents={totals?.amount_cents ?? 0}
              currency={currency}
            />

            <BreakdownTable
              title="By user"
              emptyLabel="No time logged in this range."
              rows={byUser}
              labelKey="name"
              fallbackKey="email"
              currency={currency}
            />

            <BreakdownTable
              title="By task"
              emptyLabel="No tasks with time logged in this range."
              rows={byTask}
              labelKey="title"
              fallbackKey={null}
              currency={currency}
            />
          </>
        )}
      </div>
    </>
  );
};

interface SummaryCardsProps {
  totalMinutes: number;
  billableMinutes: number;
  amountCents: number;
  currency: string | undefined;
}

const SummaryCards = ({ totalMinutes, billableMinutes, amountCents, currency }: SummaryCardsProps) => (
  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
    <SummaryCard label="Total" value={formatHours(totalMinutes)} sub={`${totalMinutes} min`} />
    <SummaryCard
      label="Billable"
      value={formatHours(billableMinutes)}
      sub={`${billableMinutes} min`}
    />
    <SummaryCard label="Amount" value={formatMoney(amountCents, currency)} sub={currency ?? ''} />
  </div>
);

const SummaryCard = ({ label, value, sub }: { label: string; value: string; sub: string }) => (
  <div className="rounded-xl border border-[var(--glass-card-border)] bg-[var(--glass-card-bg)] p-4 shadow-[var(--glass-card-shadow)] backdrop-blur-md">
    <p className="text-foreground-faint text-[11px] font-semibold tracking-[0.06em] uppercase">
      {label}
    </p>
    <p className="text-foreground mt-1.5 text-[28px] font-semibold tracking-[-0.02em] tabular-nums">
      {value}
    </p>
    {sub ? <p className="text-foreground-faint mt-0.5 text-[11px]">{sub}</p> : null}
  </div>
);

interface BreakdownTableProps {
  title: string;
  emptyLabel: string;
  rows: BucketRow[];
  labelKey: 'name' | 'title';
  fallbackKey: 'email' | null;
  currency: string | undefined;
}

const BreakdownTable = ({
  title,
  emptyLabel,
  rows,
  labelKey,
  fallbackKey,
  currency,
}: BreakdownTableProps) => (
  <section className="rounded-xl border border-[var(--glass-card-border)] bg-[var(--glass-card-bg)] shadow-[var(--glass-card-shadow)] backdrop-blur-md">
    <header className="border-b border-[var(--glass-card-border)] px-4 py-3">
      <h2 className="text-foreground text-[14px] font-semibold tracking-[-0.005em]">
        {title}
      </h2>
    </header>
    {rows.length === 0 ? (
      <p className="text-foreground-faint px-4 py-6 text-[12.5px]">{emptyLabel}</p>
    ) : (
      <table className="w-full text-[13px]">
        <thead>
          <tr className="text-foreground-faint border-b border-[var(--glass-card-border)] text-left text-[10.5px] tracking-[0.06em] uppercase">
            <th className="px-4 py-2.5 font-semibold">
              {labelKey === 'name' ? 'Member' : 'Task'}
            </th>
            <th className="px-4 py-2.5 text-right font-semibold tabular-nums">Total</th>
            <th className="px-4 py-2.5 text-right font-semibold tabular-nums">Billable</th>
            <th className="px-4 py-2.5 text-right font-semibold tabular-nums">Amount</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--glass-card-border)]">
          {rows.map((r, idx) => (
            <tr key={`${r.user_id ?? r.task_id ?? idx}`}>
              <td className="text-foreground px-4 py-2.5">
                <span>{r[labelKey] ?? '—'}</span>
                {fallbackKey && r[fallbackKey] && r[fallbackKey] !== r[labelKey] ? (
                  <span className="text-foreground-faint ml-1.5 text-[11.5px]">
                    {r[fallbackKey]}
                  </span>
                ) : null}
              </td>
              <td className="text-foreground-muted px-4 py-2.5 text-right font-mono text-[12px] tabular-nums">
                {formatHours(r.minutes)}
              </td>
              <td className="text-foreground-muted px-4 py-2.5 text-right font-mono text-[12px] tabular-nums">
                {formatHours(r.billable_minutes)}
              </td>
              <td className="text-foreground px-4 py-2.5 text-right font-mono text-[12px] font-medium tabular-nums">
                {formatMoney(r.amount_cents, currency)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </section>
);
